import { t } from '../i18n/idioma';
import type { DueloEstado, DueloRodada } from '../world/types';

interface Props {
  estado: DueloEstado;
  faltaLuta: number;
  faixa: boolean;
  lute: boolean;
  cartao: boolean;
  onSair(): void;
}

function melhorDe(estado: DueloEstado): number {
  return estado.rodadasPara * 2 - 1;
}

function decisiva(estado: DueloEstado): boolean {
  return estado.rodadasPara > 1
    && estado.placarVoce === estado.rodadasPara - 1
    && estado.placarEle === estado.rodadasPara - 1;
}

function linhaContagem(estado: DueloEstado): string {
  const n = melhorDe(estado);
  const a = estado.placarVoce;
  const b = estado.placarEle;
  if (a === 0 && b === 0)
    return estado.modo === 'com_loot'
      ? t('duelo.conta.aberturaLoot', { n, v: estado.rodadasPara })
      : t('duelo.conta.abertura', { n, v: estado.rodadasPara });
  if (decisiva(estado)) return t('duelo.conta.decisiva', { a, b });
  if (a > b) return t('duelo.conta.voceLidera', { a, b });
  if (b > a) return t('duelo.conta.eleLidera', { nome: estado.adversario, a: b, b: a });
  return t('duelo.conta.empatados', { a, b });
}

function textoPor(estado: DueloEstado, rodada: DueloRodada): string {
  if (rodada.por === 'tempo' && rodada.quem === 'empate') return t('duelo.por.tempoEmpate');
  if (rodada.por === 'tempo')
    return t('duelo.por.tempo', { a: rodada.vidaVoce, b: rodada.vidaEle, nome: estado.adversario });
  if (rodada.quem === 'voce') return t('duelo.por.eleCaiu', { nome: estado.adversario });
  return t('duelo.por.voceCaiu');
}

/** O texto do cartão do fim. Sem aposta, não fala de prêmio nem de correio: nada chega (bug de 28/10/2026). */
function textoFim(estado: DueloEstado): string {
  const nome = estado.adversario;
  const sem = estado.comAposta ? '' : 'SemAposta';
  if (estado.desfecho === 'desistencia')
    return estado.vencedor === 'voce'
      ? t(`duelo.fim.textoDesistiuEle${sem}`, { nome })
      : t(`duelo.fim.textoDesistiuVoce${sem}`, { nome });
  if (estado.desfecho === 'empate' || estado.vencedor === '')
    return t(`duelo.fim.textoEmpate${sem}`, { v: estado.rodadasPara, n: estado.rodadas.length || melhorDe(estado) });
  if (estado.vencedor === 'voce') return t(`duelo.fim.textoVitoria${sem}`);
  return t(`duelo.fim.textoDerrota${sem}`, { nome });
}

export function DueloCentro({ estado, faltaLuta, faixa, lute, cartao, onSair }: Props) {
  if (lute)
    return (
      <div className="conta">
        <p className="lute" aria-hidden="true">{t('duelo.lute')}</p>
      </div>
    );

  const ultima = estado.rodadas[estado.rodadas.length - 1];
  if (faixa && ultima) {
    const fechou = estado.placarVoce >= estado.rodadasPara || estado.placarEle >= estado.rodadasPara;
    const quem = estado.placarVoce >= estado.rodadasPara
      ? (estado.voce || t('duelo.voce'))
      : estado.adversario;
    return (
      <div className={`faixa faixa--${ultima.quem}`}>
        <p className="faixa__titulo">{
          ultima.quem === 'voce'
            ? t('duelo.faixa.voce', { n: estado.rodadas.length })
            : ultima.quem === 'ele'
              ? t('duelo.faixa.ele', { n: estado.rodadas.length, nome: estado.adversario })
              : t('duelo.faixa.empate', { n: estado.rodadas.length })
        }</p>
        <p className="faixa__por">{textoPor(estado, ultima)}</p>
        <p className="faixa__prox">
          {fechou
            ? t('duelo.faixa.leva', { quem, a: estado.placarVoce, b: estado.placarEle })
            : t('duelo.faixa.volta')}
        </p>
      </div>
    );
  }

  if (estado.estado === 'contagem' && faltaLuta > 0) {
    const totalSeg = Math.max(1, (estado.contagemMs || 5000) / 1000);
    const n = Math.max(1, Math.ceil(faltaLuta / 1000));
    const r = 66;
    const c = 2 * Math.PI * r;
    const frac = Math.max(0, Math.min(1, (n - 1) / totalSeg));
    const dec = decisiva(estado);
    return (
      <div className="conta">
        <p className={`conta__kicker${dec ? ' conta__kicker--decisiva' : ''}`}>
          {dec ? t('duelo.decisiva') : t('duelo.rodadaN', { n: estado.rodada })}
        </p>
        <div className="anel" aria-hidden="true">
          <svg viewBox="0 0 150 150">
            <circle className="fundo" cx="75" cy="75" r={r} />
            <circle
              className="frente"
              cx="75"
              cy="75"
              r={r}
              strokeDasharray={c.toFixed(1)}
              strokeDashoffset={(c * (1 - frac)).toFixed(1)}
            />
          </svg>
          <span key={n} className="anel__num">{n}</span>
        </div>
        <p className="conta__linha">{linhaContagem(estado)}</p>
      </div>
    );
  }

  if (!cartao) return null;
  const tom = estado.vencedor === 'voce' ? 'vitoria' : estado.vencedor === 'ele' ? 'derrota' : 'empate';
  const titulo = tom === 'vitoria'
    ? t('duelo.fim.vitoria')
    : tom === 'derrota' ? t('duelo.fim.derrota') : t('duelo.fim.empate');
  const modo = estado.modo === 'com_loot' ? t('duelo.comLoot') : t('duelo.semLoot');
  return (
    <div className={`fim fim--${tom}`} role="dialog" aria-labelledby="duelo-fim-titulo">
      <p className="fim__kicker">{t('duelo.fim.kicker', { modo })}</p>
      <p className="fim__titulo" id="duelo-fim-titulo">{titulo}</p>
      <div className="fim__placar">
        <span>{estado.voce || t('duelo.voce')}</span>
        <strong>{estado.placarVoce} × {estado.placarEle}</strong>
        <span>{estado.adversario}</span>
      </div>
      {estado.rodadas.length > 0 && (
        <ul className="historico" aria-label={t('duelo.historico')}>
          {estado.rodadas.map((rodada, i) => (
            <li key={i} className={`h--${rodada.quem}`}>
              <small>{t('duelo.rodadaN', { n: i + 1 })}</small>
              {rodada.quem === 'voce'
                ? t('duelo.voce')
                : rodada.quem === 'ele' ? estado.adversario : t('duelo.fim.empate')}
              {rodada.por === 'tempo' ? ` · ${t('duelo.histTempo')}` : ''}
            </li>
          ))}
        </ul>
      )}
      <p className="fim__texto">{textoFim(estado)}</p>
      <div className="fim__acoes">
        <button type="button" className="btn btn--primary" onClick={onSair}>{t('duelo.sairArena')}</button>
      </div>
    </div>
  );
}
